import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Truck, Mail, Lock, ArrowRight, MapPin, Package, Clock, User } from 'lucide-react';
import MainService from '../services/mainservice';
import { useToast } from '../contexts/ToastContext';
import Config from '../services/config.json';

const Login = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [logoLoading, setLogoLoading] = useState(true);
  const { showError, showSuccess } = useToast();

  // Fetch company profile on component mount
  useEffect(() => {
    const fetchCompanyProfile = async () => {
      try {
        setLogoLoading(true);
        const response = await MainService.GetCompanyProfile();
        if (response && response.Logo) {
          setCompanyProfile(response);
        }
      } catch (error) {
        console.error('Error fetching company profile:', error);
        // Don't show error to user as this is not critical
      } finally {
        setLogoLoading(false);
      }
    };

    fetchCompanyProfile();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      showError('Please enter both email and password');
      return;
    }

    setIsLoading(true);
    try {
      // Prepare login data
      const loginData = {
        UserName: formData.email,
        Password: formData.password
      };

      // Call the actual login API
      const response = await MainService.Login(loginData);
      
      console.log('Login response:', response);
      
      // Check if login was successful
      if (response && response.Token) {
        // Store the token and user data in localStorage
        localStorage.setItem('AINSOFT_DRIVER_TOKEN', JSON.stringify(response));
        
        // Check user role and handle accordingly
        if (response.Role === "Client") {
          // For Client role, call onLogin to go to home page
          showSuccess('Login successful! Welcome to your dashboard.');
          onLogin(response);
        } else {
          // For other roles, you can handle differently
          showError('Access denied. Client role required.');
        }
      } else {
        // Handle login failure
        showError(response?.Message || 'Invalid credentials. Please try again.');
      }
    } catch (error) {
      console.error('Login error:', error);
      showError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Green Background with Cloud Animations */}
      <div className="hidden lg:flex lg:w-1/2 bg-green-600 relative overflow-hidden">
        {/* Moving Clouds */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Cloud 1 - Large, realistic cloud structure */}
          <div className="absolute top-20 animate-cloud-1">
            <div className="relative">
              {/* Main cloud body */}
              <div className="w-48 h-20 bg-white bg-opacity-30 rounded-full"></div>
              {/* Cloud puffs */}
              <div className="absolute -top-6 left-8 w-32 h-16 bg-white bg-opacity-30 rounded-full"></div>
              <div className="absolute -top-4 right-6 w-28 h-14 bg-white bg-opacity-30 rounded-full"></div>
              <div className="absolute -top-8 left-16 w-24 h-12 bg-white bg-opacity-30 rounded-full"></div>
              <div className="absolute -top-2 right-16 w-20 h-10 bg-white bg-opacity-30 rounded-full"></div>
              <div className="absolute top-2 left-4 w-16 h-8 bg-white bg-opacity-30 rounded-full"></div>
            </div>
          </div>
          
          {/* Cloud 2 - Medium cloud */}
          <div className="absolute top-40 animate-cloud-2">
            <div className="relative">
              {/* Main cloud body */}
              <div className="w-36 h-16 bg-white bg-opacity-25 rounded-full"></div>
              {/* Cloud puffs */}
              <div className="absolute -top-5 left-6 w-24 h-12 bg-white bg-opacity-25 rounded-full"></div>
              <div className="absolute -top-3 right-4 w-20 h-10 bg-white bg-opacity-25 rounded-full"></div>
              <div className="absolute -top-6 left-12 w-18 h-9 bg-white bg-opacity-25 rounded-full"></div>
              <div className="absolute top-1 left-2 w-14 h-7 bg-white bg-opacity-25 rounded-full"></div>
            </div>
          </div>
          
          {/* Cloud 3 - Small, detailed cloud */}
          <div className="absolute top-60 animate-cloud-3">
            <div className="relative">
              {/* Main cloud body */}
              <div className="w-28 h-14 bg-white bg-opacity-20 rounded-full"></div>
              {/* Cloud puffs */}
              <div className="absolute -top-4 left-4 w-18 h-9 bg-white bg-opacity-20 rounded-full"></div>
              <div className="absolute -top-2 right-3 w-16 h-8 bg-white bg-opacity-20 rounded-full"></div>
              <div className="absolute -top-5 left-10 w-14 h-7 bg-white bg-opacity-20 rounded-full"></div>
              <div className="absolute top-1 left-1 w-12 h-6 bg-white bg-opacity-20 rounded-full"></div>
            </div>
          </div>
          
          {/* Cloud 4 - Medium cloud */}
          <div className="absolute bottom-40 animate-cloud-4">
            <div className="relative">
              {/* Main cloud body */}
              <div className="w-40 h-18 bg-white bg-opacity-25 rounded-full"></div>
              {/* Cloud puffs */}
              <div className="absolute -top-7 left-8 w-28 h-14 bg-white bg-opacity-25 rounded-full"></div>
              <div className="absolute -top-4 right-6 w-24 h-12 bg-white bg-opacity-25 rounded-full"></div>
              <div className="absolute -top-8 left-16 w-20 h-10 bg-white bg-opacity-25 rounded-full"></div>
              <div className="absolute -top-2 right-14 w-18 h-9 bg-white bg-opacity-25 rounded-full"></div>
              <div className="absolute top-2 left-3 w-16 h-8 bg-white bg-opacity-25 rounded-full"></div>
            </div>
          </div>
          
          {/* Cloud 5 - Small, fluffy cloud */}
          <div className="absolute bottom-20 animate-cloud-5">
            <div className="relative">
              {/* Main cloud body */}
              <div className="w-24 h-12 bg-white bg-opacity-20 rounded-full"></div>
              {/* Cloud puffs */}
              <div className="absolute -top-3 left-3 w-16 h-8 bg-white bg-opacity-20 rounded-full"></div>
              <div className="absolute -top-1 right-2 w-14 h-7 bg-white bg-opacity-20 rounded-full"></div>
              <div className="absolute -top-4 left-8 w-12 h-6 bg-white bg-opacity-20 rounded-full"></div>
              <div className="absolute top-1 left-1 w-10 h-5 bg-white bg-opacity-20 rounded-full"></div>
            </div>
          </div>
          
          {/* Additional smaller clouds for more variety */}
          <div className="absolute top-32 animate-cloud-2" style={{animationDelay: '-12s'}}>
            <div className="relative">
              <div className="w-20 h-10 bg-white bg-opacity-15 rounded-full"></div>
              <div className="absolute -top-3 left-3 w-12 h-6 bg-white bg-opacity-15 rounded-full"></div>
              <div className="absolute -top-1 right-2 w-10 h-5 bg-white bg-opacity-15 rounded-full"></div>
            </div>
          </div>
          
          <div className="absolute top-80 animate-cloud-4" style={{animationDelay: '-18s'}}>
            <div className="relative">
              <div className="w-32 h-14 bg-white bg-opacity-18 rounded-full"></div>
              <div className="absolute -top-4 left-4 w-20 h-8 bg-white bg-opacity-18 rounded-full"></div>
              <div className="absolute -top-2 right-3 w-16 h-6 bg-white bg-opacity-18 rounded-full"></div>
            </div>
          </div>
          
          {/* Floating particles for extra atmosphere */}
          <div className="absolute top-32 left-1/2 w-2 h-2 bg-white bg-opacity-40 rounded-full animate-ping"></div>
          <div className="absolute top-48 right-1/3 w-1.5 h-1.5 bg-white bg-opacity-30 rounded-full animate-ping" style={{animationDelay: '1s'}}></div>
          <div className="absolute bottom-32 left-1/3 w-1 h-1 bg-white bg-opacity-50 rounded-full animate-ping" style={{animationDelay: '2s'}}></div>
        </div>
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center items-center text-white p-12 w-full">
          {/* Logo */}
          <div className="mb-8 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white bg-opacity-20 rounded-full mb-6 backdrop-blur-sm">
              <img 
                src="/images/logo.png" 
                alt="logo" 
                className="w-16 h-16 object-contain"
              />
            </div>
            <h1 className="text-4xl font-bold mb-2">Delivery Portal</h1>
            <p className="text-xl text-green-100">Streamline Your Logistics</p>
          </div>

          {/* Image Placeholder */}
          <div className="w-full max-w-md flex items-center justify-center">
            <img 
              src="/images/drobo.png" 
              alt="Delivery Robot" 
              className="w-[512px] object-contain animate-float"
              style={{
                animation: 'float 6s ease-in-out forwards'
              }}
            />
          </div>

          <style jsx>{`
            @keyframes float {
              0% {
                transform: translateX(100px) translateY(0px);
                opacity: 0;
              }
              20% {
                transform: translateX(0px) translateY(0px);
                opacity: 1;
              }
              80% {
                transform: translateX(0px) translateY(-10px);
                opacity: 1;
              }
              100% {
                transform: translateX(0px) translateY(0px);
                opacity: 1;
              }
            }
          `}</style>

          {/* Features */}
          <div className="mt-6 grid grid-cols-1 gap-4 w-full max-w-md">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <div className="w-2 h-2 bg-white rounded-full"></div>
              </div>
              <span className="text-green-100">Real-time tracking</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <div className="w-2 h-2 bg-white rounded-full"></div>
              </div>
              <span className="text-green-100">Instant notifications</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <div className="w-2 h-2 bg-white rounded-full"></div>
              </div>
              <span className="text-green-100">Analytics dashboard</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-gradient-to-br from-gray-50 to-primary-50">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            {/* Client Logo for Mobile */}
            {companyProfile && companyProfile.Logo ? (
              <div className="mb-5 text-left">
                <div className="inline-flex items-center justify-center p-4 bg-white rounded-xl shadow-md border border-gray-100">
                  <img 
                    src={`${Config.API_BASE_URL}${companyProfile.Logo}`}
                    alt="Company Logo" 
                    className="max-h-24 max-w-64 object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full mb-5 shadow-lg">
                <Truck className="w-10 h-10 text-white" />
              </div>
            )}
            <h1 className="text-2xl font-bold text-gray-600">Welcome Back{companyProfile?.Name ? ` To ${companyProfile.Name}` : ''}</h1>
            <p className="text-gray-600">Sign in to your delivery portal</p>
          </div>

          {/* Client Logo */}
          {companyProfile && companyProfile.Logo && (
            <div className="hidden lg:block mb-6 text-left">
              <div className="inline-flex items-center justify-center p-6 bg-white rounded-xl shadow-md border border-gray-100">
                <img 
                  src={`${Config.API_BASE_URL}${companyProfile.Logo}`}
                  alt="Company Logo" 
                  className="max-h-28 max-w-80 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            </div>
          )}

          {/* Desktop Header */}
          <div className="hidden lg:block mb-8">
            <h1 className="text-3xl font-bold text-gray-600 mb-2">Welcome Back{companyProfile?.Name ? ` To ${companyProfile.Name}` : ''}</h1>
            <p className="text-gray-600">Sign in to your delivery portal</p>
          </div>

          {/* Login Form */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                 User Name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                    placeholder="Enter your username"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-600">Remember me</span>
                </label>
                <a href="/forget-password" className="text-sm text-primary-600 hover:text-primary-700 font-medium">
                  Forgot password?
                </a>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Signing in...
                  </div>
                ) : (
                  <>
                    <span>Sign In</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 text-center space-y-2">
              
              <p className="text-sm text-gray-600">
                Don't have an account?{' '}
                <a href="/register" className="text-primary-600 hover:text-primary-700 font-medium">
                  Register now
                </a>
              </p>
            </div>
          </div>

     
        </div>
      </div>
    </div>
  );
};

export default Login; 